// src/hooks/useEnergyReductionAligned.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const FIN_EN_BY_COMSET_QUERY = gql`
  query FinEnVectors($scenario: String, $from: Int, $to: Int) {
    timeSeries(
      view: FIN_EN_BY_COMSET
      scenario: $scenario
      fromYear: $from
      toYear: $to
    ) {
      year
      group   # e.g. FE_SER-ELC, FE_COM-GAS, FE_RES-OIL
      value   # PJ
    }
  }
`;

const EXCLUDE_GROUPS = new Set(["FE_COM-NEU"]); // 🚫 exclude commercial nuclear only
const PJ_TO_TWH = 0.277778;

function pctReduction(fromVal, toVal) {
  if (typeof fromVal !== "number" || !isFinite(fromVal) || fromVal === 0) return null;
  return ((fromVal - toVal) / fromVal) * 100;
}

function nearestYear(target, candidates) {
  if (!candidates.length) return null;
  let best = candidates[0], bestDiff = Math.abs(candidates[0] - target);
  for (let i = 1; i < candidates.length; i++) {
    const d = Math.abs(candidates[i] - target);
    if (d < bestDiff) { best = candidates[i]; bestDiff = d; }
  }
  return best;
}

export function useEnergyReductionAligned({
  scenario,
  from = 2025,
  to = 2050,
  baselineYearPref = 2025,
}) {
  const { data, loading, error } = useQuery(FIN_EN_BY_COMSET_QUERY, {
    variables: { scenario, from, to },
    fetchPolicy: "cache-first",
    skip: !scenario,
  });

  // Sum ALL comsets (no exclusions), convert PJ -> TWh
  const byYear = useMemo(() => {
    const map = new Map(); // year -> total TWh
    for (const r of data?.timeSeries ?? []) {
      const y = r?.year;
      if (typeof y !== "number") continue;
         const g = String(r?.group ?? "").trim();
      if (EXCLUDE_GROUPS.has(g)) continue; // skip FE_COM-NEU
      const v = typeof r?.value === "number" ? r.value * PJ_TO_TWH : 0;
      map.set(y, (map.get(y) ?? 0) + v);
    }
    return map;
  }, [data]);

  const years = useMemo(() => Array.from(byYear.keys()).sort((a, b) => a - b), [byYear]);

  const baselineYearUsed = useMemo(() => {
    if (!years.length) return null;
    if (years.includes(baselineYearPref)) return baselineYearPref;
    return nearestYear(baselineYearPref, years);
  }, [years, baselineYearPref]);

  const baselineVal = baselineYearUsed == null ? null : (byYear.get(baselineYearUsed) ?? null);

 const totalByYearTWh = useMemo(() => {
    const map = new Map();
    for (const r of data?.timeSeries ?? []) {
      const y = r?.year;
      if (typeof y !== "number") continue;
      const g = String(r?.group ?? "");
      if (EXCLUDE_GROUPS.has(g)) continue;
      const v = typeof r?.value === "number" ? r.value * PJ_TO_TWH : 0;
      map.set(y, (map.get(y) ?? 0) + v);
    }
    return map;
  }, [data]);

  const pctByYear = useMemo(() => {
    const map = new Map();
    if (baselineVal == null) return map;
    for (const y of years) {
      const val = byYear.get(y) ?? null;
      const pct = val == null ? null : pctReduction(baselineVal, val);
      map.set(y, pct);
    }
    return map;
  }, [years, byYear, baselineVal]);

  const latestYear = years.length ? years[years.length - 1] : null;
  const latestPct = latestYear != null ? (pctByYear.get(latestYear) ?? null) : null;

  return {
    years,
    pctByYear,         // % reduction vs baseline (higher = more reduction)
    baselineYearUsed,
    totalByYearTWh,
    latestPct,
    hasData: years.length > 0,
    loading,
    error,
  };
}
